(ns app.api-webhook.page
  "UI for managing outgoing API webhook URLs."
  (:require [app.api-webhook.validation :as validation]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.ui.core :as ui]
            [storrito.ui.table :as ui-table]
            [ui.app-frame :as app-frame]))

(def api-webhooks-query
  {:query/kind :query/api-webhooks})

(def refresh-action
  [:data/query api-webhooks-query])

(defn add-command
  [state]
  {:command/kind :command/api-webhook-add
   :command/data {:url (::url state)}})

(defn delete-command
  [endpoint]
  {:command/kind :command/api-webhook-delete
   :command/data {:webhook-endpoint/uuid (:webhook-endpoint/uuid endpoint)}})

(defn test-command
  [endpoint]
  {:command/kind :command/api-webhook-test
   :command/data {:webhook-endpoint/uuid (:webhook-endpoint/uuid endpoint)}})

(defn event-badge
  [event-type]
  [:span
   {:class ["inline-flex" "items-center" "rounded-full" "bg-indigo-50" "px-2"
            "py-1" "text-xs" "font-medium" "text-indigo-700"]}
   (get validation/event-names event-type (name event-type))])

(defn event-badges
  [event-types]
  (into [:div {:class ["flex" "flex-wrap" "gap-1"]}]
        (map event-badge)
        event-types))

(defn missing-social-post-events?
  [endpoint]
  (not-any? #{validation/social-post-status-changed-event-type}
            (:webhook-endpoint/event-types endpoint)))

(defn endpoint-row
  [state endpoint]
  (let [delete-cmd (delete-command endpoint)
        test-cmd (test-command endpoint)]
    [:tr
     {:class ui-table/tr
      :replicant/key (:webhook-endpoint/uuid endpoint)}
     [:td {:class (concat ui-table/td ["max-w-xl"])}
      [:div {:class ["truncate" "font-medium"]}
       (:webhook-endpoint/url endpoint)]]
     [:td {:class ui-table/td}
      (event-badges (:webhook-endpoint/event-types endpoint))]
     [:td {:class ui-table/td}
      [:div {:class ["flex" "items-center" "justify-end" "gap-3"]}
       [:button
        {:class (concat ui/button-secondary-xs ["whitespace-nowrap"])
         :disabled (command/issued? state test-cmd)
         :on {:click [[:data/command test-cmd
                       {:on-error [[:ui/show-error]]}]]}}
        (if (command/issued? state test-cmd)
          "Sending..."
          "Send test")]
       [:button
        {:class ["text-sm" "font-medium" "text-red-600" "hover:text-red-500"
                 "cursor-pointer"]
         :on {:click [[:js/confirm "Delete this webhook URL?"]
                      [:data/command delete-cmd
                       {:on-success [refresh-action]
                        :on-error [[:ui/show-error]]}]]}}
        "Delete"]]]
     [:td {:class ui-table/td}
      (cond
        (command/success? state test-cmd)
        [:span {:class ["text-sm" "text-green-600"]}
         "Test delivered"]

        (command/error? state test-cmd)
        [:span {:class ["text-sm" "text-red-600"]}
         (command/get-latest-error state test-cmd)]

        :else
        [:span {:class ["text-sm" "text-gray-400"]}
         ""])]]))

(defn endpoints-table
  [state endpoints]
  [:table
   {:class ui-table/table}
   [:thead {:class ui-table/thead}
    [:tr
     [:th {:scope "col" :class ui-table/th} "Webhook URL"]
     [:th {:scope "col" :class ui-table/th} "Events"]
     [:th {:scope "col" :class (concat ui-table/th ["text-right"])}]
     [:th {:scope "col" :class ui-table/th} "Last test"]]]
   (into [:tbody {:class ui-table/tbody}]
         (if (seq endpoints)
           (mapv (fn [endpoint]
                   (endpoint-row state endpoint))
                 endpoints)
           [[:tr {:class ui-table/tr}
             [:td {:colSpan 4
                   :class (concat ui-table/td ["font-medium"])}
              "No webhook URLs yet. Add one above."]]]))])

(defn add-form
  [state result]
  (let [url (or (::url state) "")
        endpoints (or (:webhook-endpoints result) [])
        limit (or (:max-webhook-endpoints result)
                  validation/max-endpoint-count)
        limit-reached? (>= (count endpoints) limit)
        cmd (add-command state)
        url-error (validation/url-error-message url)
        can-submit? (and (seq url)
                         (nil? url-error)
                         (not limit-reached?))]
    [:div
     {:class ["bg-white" "rounded-xl" "shadow-sm" "border" "border-gray-200"
              "overflow-hidden"]}
     [:div {:class ["px-5" "py-4" "border-b" "border-gray-100"]}
      [:h2 {:class ["text-lg" "font-semibold" "text-gray-900"]}
       "Add webhook URL"]
      [:p {:class ["text-sm" "text-gray-500" "mt-0.5"]}
       "Storrito will POST status updates to this HTTPS URL."]]
     [:div {:class ["px-5" "py-4"]}
      [:div {:class ["flex" "flex-col" "sm:flex-row" "gap-3"]}
       [:input
        {:type "url"
         :value url
         :placeholder "https://hook.make.com/..."
         :disabled limit-reached?
         :class ["block" "w-full" "rounded-lg" "border" "border-gray-300"
                 "px-3" "py-2.5" "text-sm" "text-gray-900" "shadow-sm"
                 "placeholder:text-gray-400"
                 "focus:ring-2" "focus:ring-indigo-600" "focus:border-indigo-600"
                 "disabled:bg-gray-100"]
         :on {:input [[:store/assoc ::url :event/target.value]]}}]
       [:button
        {:class ui/button-primary
         :disabled (or (not can-submit?)
                       (command/issued? state cmd))
         :on {:click (when can-submit?
                       [[:data/command cmd
                         {:on-success [refresh-action
                                       [:store/dissoc ::url]]
                          :on-error [[:ui/show-error]]}]])}}
        (if (command/issued? state cmd)
          "Adding..."
          "Add webhook")]]
      [:div {:class ["mt-2" "text-sm"]}
       (cond
         limit-reached?
         [:span {:class ["text-amber-700"]}
          "You can add up to " limit " webhook URLs."]

         (and (seq url) url-error)
         [:span {:class ["text-red-600"]}
          url-error]

         (command/error? state cmd)
         [:span {:class ["text-red-600"]}
          (command/get-latest-error state cmd)]

         :else
         [:span {:class ["text-gray-500"]}
          (str (count endpoints) " of " limit " webhook URLs used.")])]]]))

(defn panel
  [state]
  (let [result (query/get-result state api-webhooks-query)
        endpoints (or (:webhook-endpoints result) [])]
    [:div
     [:div {:class ["mb-6" "flex" "items-start" "justify-between" "gap-4"]}
      [:div
       [:h1 {:class ui/h1} "Webhooks"]
       [:p {:class ["mt-2" "text-gray-700" "max-w-2xl"]}
        "Use webhooks to update Notion, Make, Zapier, n8n, or your own backend as soon as an Instagram Story post or a social post (Facebook, LinkedIn, Threads, ...) changes status."]]
      [:a {:class ui/a-indigo
           :href "/documentation/api/v1/#webhooks"
           :target "_blank"}
       "Webhook docs"]]

     [:div {:class ["space-y-6"]}
      (add-form state result)
      [:div
       {:class ["bg-white" "rounded-xl" "shadow-sm" "border" "border-gray-200"
                "overflow-hidden"]}
       [:div {:class ["px-5" "py-4" "border-b" "border-gray-100"]}
        [:h2 {:class ["text-lg" "font-semibold" "text-gray-900"]}
         "Webhook URLs"]
        [:p {:class ["text-sm" "text-gray-500" "mt-0.5"]}
         "Each URL receives the events listed next to it."]
        (when (some missing-social-post-events? endpoints)
          [:p {:class ["text-sm" "text-amber-700" "mt-1"]}
           "URLs added before social post events existed receive Instagram Story events only. Delete and add them again to also receive "
           validation/social-post-status-changed-event-name "."])]
       [:div {:class ["overflow-x-auto"]}
        (endpoints-table state endpoints)]]]]))

(defn render
  [state]
  (app-frame/app-frame
    state
    (if (query/loading? state api-webhooks-query)
      [:div
       {:class ["flex" "items-center" "justify-center" "py-20"]}
       [:div {:class ["text-gray-400"]} "Loading..."]]
      (panel state))))

(def page
  {:page-id :pages/api-webhooks
   :route [["ui" "api-webhook" "manage"]]
   :on-load (fn [_location]
              [[:ui/hide-sidebar-on-mobile]
               [:store/dissoc ::url]
               [:data/query api-webhooks-query]])
   :render #'render})
